import React from "react";
import "./accessibility.css";
import { motion } from "motion/react";
import { GlowingEffect } from "../ui/glowing-effect";
import Seo from "../../seo/Seo";

function FadeIn({ children, delay = 0, className }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 120, damping: 14, delay }}
    >
      {children}
    </motion.div>
  );
}

const sections = [
  {
    title: "Our Commitment",
    body: `RAIN Careers is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to make our platform as inclusive as possible.`,
  },
  {
    title: "Conformance Status",
    body: `We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA. These guidelines explain how to make web content more accessible to people with disabilities. Conformance with these guidelines helps make the web more user-friendly for all people.`,
  },
  {
    title: "Measures We Take",
    items: [
      "Provide keyboard navigation support throughout the application.",
      "Maintain sufficient color contrast ratios for all text and UI elements.",
      "Include descriptive alt text for all meaningful images.",
      "Use semantic HTML to ensure compatibility with screen readers.",
      "Support text resizing up to 200% without loss of content or functionality.",
      "Avoid content that flashes more than three times per second.",
      "Provide clear focus indicators for interactive elements.",
      "Label all form inputs clearly and associate them with their fields.",
    ],
  },
  {
    title: "Assistive Technology Compatibility",
    body: `RAIN Careers is designed to be compatible with the following assistive technologies: screen readers (such as NVDA, JAWS, and VoiceOver), screen magnification tools, speech recognition software, and keyboard-only navigation. We test regularly with these tools to identify and resolve barriers.`,
  },
  {
    title: "Known Limitations",
    body: `While we strive to ensure all content is accessible, some third-party content or embedded tools may not fully meet our accessibility standards. We are actively working with our vendors and partners to address these gaps. If you encounter an issue, please let us know so we can prioritize a fix.`,
  },
  {
    title: "Feedback & Contact",
    body: `We welcome feedback on the accessibility of RAIN Careers. If you experience any barriers or have suggestions for improvement, please reach out to us at accessibility@raincareers.com. We aim to respond to accessibility feedback within 5 business days and will work with you to provide an accessible alternative if needed.`,
  },
  {
    title: "Formal Complaints",
    body: `If you are not satisfied with our response to your accessibility feedback, you may contact the relevant national authority in your jurisdiction. In the United States, this may include filing a complaint with the U.S. Department of Justice under the Americans with Disabilities Act (ADA).`,
  },
];

const Accessibility = () => {
  return (
    <div className="accessibility-page">
      <Seo path="/accessibility" />
      {/* Header */}
      <section className="accessibility-header">
        <div className="accessibility-header-inner">
          <FadeIn delay={0}>
            <p className="accessibility-subheading">Legal</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="accessibility-heading">Accessibility Statement</h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="accessibility-meta">
              Last updated: March 2026 &nbsp;·&nbsp; RAIN Careers, Inc.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Content */}
      <div className="accessibility-content">
        {sections.map((section, i) => (
          <FadeIn delay={0.05 * i} key={i}>
            <section className="accessibility-section">
              <GlowingEffect spread={30} borderWidth={1.5} />
              <h2>{section.title}</h2>
              {section.body && <p>{section.body}</p>}
              {section.items && (
                <ul>
                  {section.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          </FadeIn>
        ))}
      </div>
    </div>
  );
};

export default Accessibility;
